import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { skip, Subscription } from 'rxjs';
import { ShareService } from '@core/services/share-data/share-data.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ModalService } from '@core/services/modal/modal.service';
import { QuizModalComponent } from '@shared/modals/quiz-modal/quiz-modal.component';
import { QuizCloseButton, TaskbarItem } from '@root/app.interfaces';
import { SELECTORS, STORAGE_NAMES, VIEW_TYPES } from '@root/app.config';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { Location } from '@angular/common';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { PassDataService } from '@core/services/pass-data/pass-data.service';
import { SetupPageService } from '@core/services/setup-page/setup-page.service';
import { GtmService } from '@core/services/gtm/gtm.service';
import { GTM_HEADER_EVENTS } from '@core/services/gtm/gtm.events';

@Component({
	selector: 'app-taskbar',
	templateUrl: './taskbar.component.html',
	styleUrls: ['./taskbar.component.scss', './taskbar.responsive.scss'],
})
export class TaskbarComponent implements OnInit, OnDestroy {
	@ViewChild('taskbarPanelBlock') taskbarPanelBlock: ElementRef<HTMLElement>;
	@ViewChild('feedbackTooltip') feedbackTooltip: NgbPopover;

	getQuizHandler: Subscription;
	getViewType: Subscription;
	getTaskbarPanel: Subscription;
	getToolBarEvent: Subscription;
	getFeedbackTooltipStatus: Subscription;
	getSelectOpeningMode: Subscription;

	isMobile: boolean = this.passDataService.isMobileOrTablet;
	isLargeTablet: boolean = this.setupPageService.largeTabletCheck();
	isDesignType = false;
	isSelectOpeningMode = false;
	taskbarHasUpdate = false;
	taskbarContainer = false;
	taskbarPanel = true;
	taskbarControls = false;

	taskbarItems: TaskbarItem[] = [
		{
			title_translation: 'taskbar.items.control_panel.title',
			id: 'control',
			active: false,
			status: null,
			button: {
				type: 'yellow',
				text_translation: 'taskbar.items.control_panel.button.show',
			},
		},
		{
			title_translation: 'taskbar.items.onboarding.title',
			id: 'help',
			active: false,
			status: null,
			button: {
				type: 'white',
				text_translation: 'taskbar.items.onboarding.button.review',
			},
		},
	];

	get quizTypeKey(): 'dyb' | 'detection' | 'iv' {
		return this.isDesignType ? 'dyb' : this.isSelectOpeningMode ? 'detection' : 'iv';
	}

	constructor(
		private sessionStorageService: SessionStorageService,
		private setupPageService: SetupPageService,
		private passDataService: PassDataService,
		private modalService: ModalService,
		private shareService: ShareService,
		private matDialog: MatDialog,
		private location: Location,
		private gtm: GtmService,
	) {}

	@HostListener('document:click', ['$event.target'])
	onClick(targetElement: HTMLElement): void {
		const isClickedInside = this.taskbarPanelBlock?.nativeElement.contains(targetElement);
		const isClickedPopover = targetElement.closest(SELECTORS.feedback_popover);

		if (!isClickedInside && !isClickedPopover) {
			this.taskbarContainer = isClickedInside;
		}
	}

	@HostListener('window:resize', ['$event'])
	onResize() {
		this.isLargeTablet = this.setupPageService.largeTabletCheck();
		this.openFeedbackTooltip();

		const currentItem = this.taskbarItems.find((item) => item.id === 'control');
		this.taskbarControls = !this.taskbarControls;
		this.onControlPanelHandler(currentItem);
	}

	ngOnInit(): void {
		this.currentLinkHandler();

		// Skip the initial value emitted by the BehaviorSubject to prevent a visual glitch in the component.
		this.getSelectOpeningMode = this.shareService.getSelectOpeningMode.pipe(skip(1)).subscribe((res) => (this.isSelectOpeningMode = res));

		this.getTaskbarPanel = this.shareService.getTaskbarPanel.subscribe((res: boolean) => {
			this.taskbarPanel = res;

			if (this.taskbarPanel) {
				this.resetControlStatus('control');
			}
		});
		this.getViewType = this.shareService.getViewType.subscribe(this.viewTypeChangeHandler.bind(this));

		this.getQuizHandler = this.shareService.getQuizStatus.subscribe((data: QuizCloseButton) => {
			if (!data.status) {
				this.taskbarHasUpdate = !data.status;

				const getQuiz = this.taskbarItems.find((item) => item.id === 'help');
				getQuiz.status = !data.status;

				if (data?.reviewed) {
					getQuiz.status = false;
					this.taskbarUpdatesHandler();
				}
			} else {
				const getQuiz = this.taskbarItems.find((item) => item.id === 'help');
				getQuiz.status = false;
				this.taskbarUpdatesHandler();
			}
		});

		this.getToolBarEvent = this.shareService.getToolBarEvent.subscribe((status: boolean) => {
			const currentItem = this.taskbarItems.find((item) => item.id === 'control');
			currentItem.active = status;
			this.taskbarControls = !status;

			this.onControlPanelHandler(currentItem, false);
		});

		this.feedbackTooltipHandler();
	}

	ngOnDestroy() {
		this.getViewType.unsubscribe();
		this.getQuizHandler.unsubscribe();
		this.getTaskbarPanel.unsubscribe();
		this.getToolBarEvent.unsubscribe();
		this.getSelectOpeningMode.unsubscribe();
		this.getFeedbackTooltipStatus.unsubscribe();
	}

	currentLinkHandler(): void {
		const currentDomainLink = this.location.path().split('?');
		const currentLink = currentDomainLink[0].split('/')[1] || 'home';
		if (['overview', 'quote'].includes(currentLink)) {
			this.taskbarPanel = false;
		} else if (!this.matDialog.getDialogById(QuizModalComponent.ID)) {
			const getCurrentViewType = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_view_type);
			this.viewTypeChangeHandler(getCurrentViewType);
		}
	}

	onControlHandler(event: Event): void {
		const buttonElement = event.currentTarget as HTMLButtonElement;
		const currentItem = this.taskbarItems.find((item) => item.id === buttonElement.id);

		currentItem.active = !currentItem.active;

		if (buttonElement.id === 'control') {
			this.onControlPanelHandler(currentItem);

			this.gtm.pushEvent(GTM_HEADER_EVENTS.CONTROL_PANEL_VISIBLE);
		}

		if (buttonElement.id === 'help') {
			this.onOnboardingHandler(currentItem);

			this.gtm.pushEvent(GTM_HEADER_EVENTS.CONTROL_PANEL_HELP);
		}
	}

	resetControlStatus(id: string) {
		const currentItem = this.taskbarItems.find((item) => item.id === id);

		if (currentItem.id === 'control') {
			this.taskbarControls = true;
			currentItem.active = false;
			this.onControlPanelHandler(currentItem);
		}
	}

	onControlPanelHandler(sectionObject: TaskbarItem, setControlsStatus: boolean = true): void {
		this.taskbarControls = !this.taskbarControls;

		if (setControlsStatus) {
			this.shareService.setTaskbarControls(this.taskbarControls);
		}

		if (this.taskbarControls) {
			this.taskbarHasUpdate = true;
		}

		sectionObject.status = this.taskbarControls;
		sectionObject.button.text_translation = this.taskbarControls
			? 'taskbar.items.control_panel.button.hide'
			: 'taskbar.items.control_panel.button.show';

		this.taskbarUpdatesHandler();
	}

	onOnboardingHandler(sectionObject: TaskbarItem): void {
		if (!this.matDialog.getDialogById(QuizModalComponent.ID)) {
			this.matDialog.open(QuizModalComponent, this.modalService.getConfig(QuizModalComponent.ID));

			sectionObject.status = false;
			this.taskbarUpdatesHandler();
		}
	}

	taskbarUpdatesHandler(): void {
		const panelHasUpdates = this.taskbarItems.find((item) => item.status);

		this.taskbarHasUpdate = panelHasUpdates?.status || false;
	}

	viewTypeChangeHandler(type): void {
		const quizReviewedStatus = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_quiz_reviewed_status);
		this.isDesignType = type === VIEW_TYPES.design;

		const reviewedStatus = quizReviewedStatus?.[this.quizTypeKey];
		const getQuiz = this.taskbarItems.find((item) => item.id === 'help');
		getQuiz.status = !reviewedStatus;

		this.taskbarUpdatesHandler();
	}

	onTaskbarHandler(event: Event = null) {
		event?.preventDefault();

		this.closeFeedbackTooltip();
		this.taskbarContainer = !this.taskbarContainer;

		this.gtm.pushEvent(GTM_HEADER_EVENTS.CONTROL_PANEL);
	}

	feedbackTooltipHandler(): void {
		this.openFeedbackTooltip();

		this.getFeedbackTooltipStatus = this.shareService.getFeedbackTooltipStatus.subscribe((status: boolean) => {
			this.passDataService.isFeedbackTooltipStatus = status;
			this.openFeedbackTooltip();
		});
	}

	openFeedbackTooltip(): void {
		const popoverComponentStatus = this.feedbackTooltip && this.feedbackTooltip.ngbPopover && !this.feedbackTooltip.isOpen();

		if (this.passDataService.isFeedbackTooltipStatus && popoverComponentStatus) {
			this.feedbackTooltip.open();
		}
	}

	closeFeedbackTooltip(event: Event = null): void {
		event?.stopImmediatePropagation();

		if (this.feedbackTooltip && this.feedbackTooltip.ngbPopover) {
			this.passDataService.isFeedbackTooltipStatus = false;
			this.feedbackTooltip.close();
		}
	}
}
